// Product analytics (Mixpanel) and error monitoring (Sentry), both lazy loaded and off without their env vars:
//   VITE_MIXPANEL_TOKEN turns Mixpanel on, VITE_MIXPANEL_REPLAY_PERCENT samples session replay (default 100),
//   VITE_SENTRY_DSN turns Sentry on. Without them every function here is a cheap no op.
// Mixpanel loads only once the visitor's analytics decision allows it (lib/consent.ts, lib/consentStore.ts): on from
// the first page where consent does not come first (no banner: the Privacy Policy explains it and people agree to it
// when they join, MG 2026-10-07), after a yes in Settings or Analytics choices where it does. Saying no opts out,
// stops replay and clears Mixpanel's browser data. Session replay follows the same decision, masks all text, records
// whatever carries a person or a secret in an attribute as an empty box (REPLAY_BLOCK_SELECTOR) and never records
// while the address bar holds anything but a known safe query parameter.
// mixpanel-browser is pinned to 2.83.0 (package.json): the replay stop below reaches into its recorder.
// docs/analytics.md has the event dictionary, the masking rules and how MG queries the data.
import { HIDDEN_ATTRS, HIDE_ATTR, MASK_ATTR, PRIVATE_ATTRS } from '@/components/ui/privateArea'
import { testHook } from './consent'
import { analyticsAllowed, effectiveAllowed, useConsentStore } from './consentStore'

type Props = Record<string, unknown>
type Mixpanel = typeof import('mixpanel-browser').default
type Sentry = typeof import('@sentry/react')

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined

/** Errors Sentry drops: browser faults this app cannot act on. Safari ends a page's IndexedDB connection while the Mac
 *  sleeps, and every later database call in that page fails with the first message. Only session replay keeps data in
 *  IndexedDB, and mixpanel-browser 2.83 leaves that failure unhandled when its 24 hour or idle reset clears the replay
 *  queue; nothing on the page breaks and replay carries on in memory (Sentry WEB-1, 2026-10-14).
 *  An AbortError is a request this app cancelled on purpose (React Query cancels a read in flight when the same data
 *  is read again). The app handles it; a browser extension that wraps fetch left that rejection unhandled, and
 *  Sentry reported it (WEB-4, WEB-5, 2026-10-25). */
export const SENTRY_IGNORE_ERRORS: RegExp[] = [/Connection to Indexed Database server lost/, /^AbortError: /]
/** Scripts Sentry ignores errors from: browser extensions running in the page (WEB-3, 2026-10-25: an extension's fetch
 *  wrapper reported "Failed to fetch" while the API restarted). */
export const SENTRY_DENY_URLS: RegExp[] = [/^(?:chrome|moz|safari(?:-web)?)-extension:\/\//i]
const BUILD = (import.meta.env.VITE_BUILD as string | undefined) || undefined

/** Containers whose clicks autocapture keeps without any label, link or name (settings, billing, checkout, the admin
 *  console, the auth pages, anything that shows an email address): spread it on the element, and put a PrivateScope
 *  (@/components/ui) inside it so the dialogs, sheets, popovers, menus and tooltips it opens (portals, outside the
 *  element) carry it too. Session replay masks their text like all text, but records attributes as they are: no
 *  address, message or secret in an aria-label, title or link there (name it with aria-labelledby or
 *  aria-describedby, or hide the area). */
export { MASK_ATTR }
export const PRIVATE_AREA = PRIVATE_ATTRS
/** Chats and direct messages: session replay records an empty box in their place (no text, attributes, links or file
 *  names), and clicks keep no label, link or name. Spread it on the element and put a <PrivateScope hide> inside. */
export const HIDDEN_AREA = HIDDEN_ATTRS
/** An element whose own label, title or link holds a person's name, a file name, a code or something someone wrote (a
 *  member's menu button, a notification row, a participant tile, a certificate): session replay records it as an
 *  empty box and its clicks keep no label, link or name, while screen readers keep its full label. Spread it on the
 *  element itself (code review 4). */
export const NAMED_ELEMENT = HIDDEN_ATTRS
/** Elements replay replaces with an empty box and autocapture skips (QR codes, authenticator keys, recovery codes,
 *  the calendar feed link): spread it on them. */
export const BLOCK_ATTR = 'data-mp-block'
export const BLOCK_AREA = { [BLOCK_ATTR]: 'true' } as const
/** Static screen text with no user data (navigation, the analytics banner): session replay shows it, while every
 *  other text is masked. Never around anything that can show a name, an address or something someone wrote. */
export const UNMASK_ATTR = 'data-mp-unmask'
export const UNMASK_AREA = { [UNMASK_ATTR]: 'true' } as const

/** The address the visitor landed on, read once when the app loads (the first touch keeps its campaign tags even
 *  when consent comes later in the visit, after the page moved on). */
const LANDING = typeof window !== 'undefined'
  ? { search: window.location.search, pathname: window.location.pathname, origin: window.location.origin }
  : null
export function landingLocation(): { search: string; pathname: string; origin: string } | null {
  return LANDING
}

/** Query parameters session replay may see: ids, pages, tabs, filters and campaign tags (utm_*). Any other one (an
 *  invite, a token, an address, a search, ?next= that can carry them, ?ref= with an affiliate's handle) pauses replay
 *  while it is in the address bar: replay uploads the page address with every batch, query string included
 *  (SEC2-CONFIG-03). */
const REPLAY_SAFE_PARAMS: ReadonlySet<string> = new Set([
  'at', 'c', 'category', 'checkout', 'comment', 'community', 'course', 'date', 'declined', 'edit', 'error', 'event',
  'free', 'from', 'invoice', 'join', 'language', 'lesson', 'member', 'month', 'p', 'page', 'panel', 'plans', 'portal',
  'preview', 'price', 'review', 'role', 's', 'sort', 'status', 'tab', 'tag', 'theme', 'tier', 'to', 'type',
  'view', 'welcome',
])
/** Pages that never record: reset, verify and unsubscribe links carry a secret, live calls promise their chat is not
 *  saved, and a post's, a profile's or a certificate's address holds the post title, the member's handle or the
 *  certificate code, which replay takes straight from the address bar (re-audit major 6, minor 10). */
const NO_REPLAY_PAGES =
  /^\/(reset-password|verify-email|unsubscribe)(\/|$)|^\/[^/]+\/live(\/|$)|^\/[^/]+\/post\/|^\/u\/|^\/certificates\//

/** A path with what it names replaced by the route's pattern: a post's address holds its title
 *  (/mg-ai/post/<title>, backend services/posts.py), a profile's the member's handle and a certificate's its code.
 *  Every path and address of this site analytics send goes through it (re-audit major 6, minor 10). */
export function redactPath(path: string): string {
  if (/^\/u\/[^/?#]+/.test(path)) return path.replace(/^\/u\/[^/?#]+/, '/u/:handle')
  if (/^\/certificates\/[^/?#]+/.test(path)) return path.replace(/^\/certificates\/[^/?#]+/, '/certificates/:code')
  return path.replace(/^\/([^/?#]+)\/post\/[^/?#]+/, '/$1/post/:post')
}

export function mixpanelToken(): string | undefined {
  return testHook()?.token || (import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined) || undefined
}

/** True when this build can send product analytics at all (the banner and the Settings switch show only then). */
export function analyticsConfigured(): boolean {
  return !!mixpanelToken()
}

/** Share of page loads that record a session replay, 0 to 100 (VITE_MIXPANEL_REPLAY_PERCENT, default 100). */
export function replayPercent(): number {
  const raw = testHook()?.replayPercent ?? (import.meta.env.VITE_MIXPANEL_REPLAY_PERCENT as string | undefined)
  const n = raw === undefined || raw === '' ? 100 : Number(raw)
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 100
}

/** A URL without its query string and fragment. They can hold reset, verify, invite and unsubscribe tokens
 *  (SEC2-CONFIG-03), so analytics and error reports only ever see the path. */
export function stripQuery(url: string | null | undefined): string {
  return (url ?? '').split(/[?#]/, 1)[0]
}

/** Query free forms of the address bar URL and referrer, which Mixpanel would otherwise add to every event (the
 *  path of this site's addresses redacted, redactPath). */
export function safeUrlProps(): Record<string, string> {
  const props: Record<string, string> = { $current_url: `${window.location.origin}${redactPath(window.location.pathname)}` }
  if (document.referrer) props.$referrer = scrubText(document.referrer, '$referrer')
  return props
}

/** True while session replay must not record this address: a page above, a query parameter outside
 *  REPLAY_SAFE_PARAMS, or a fragment that carries values. */
export function hasSecretQuery(search: string, pathname = '', hash = ''): boolean {
  if (NO_REPLAY_PAGES.test(pathname) || hash.includes('=')) return true
  for (const name of new URLSearchParams(search).keys()) {
    if (!REPLAY_SAFE_PARAMS.has(name) && !name.startsWith('utm_')) return true
  }
  return false
}

const URL_LIKE = /^(https?:\/\/|\/)/
const WEB_URL = /^https?:\/\//i
/** Properties that hold a link or an address ($current_url, $referrer, the href of a clicked link): their query
 *  string always goes. */
const LINK_KEY = /(href|url|referrer)$/i
const SCHEME = /^([a-z][a-z0-9+.-]*):/i
/** Links that are not web pages carry their secret in the link itself (otpauth: holds an authenticator key and the
 *  address, webcal: a calendar feed token, mailto: an address): every scheme but http and https keeps only its name,
 *  in a link property always, elsewhere when the value is an address (scheme://...) or one of these schemes that
 *  have no //. Text such as "mg-ai:owner" stays as it is. */
const OPAQUE_SCHEME = new RegExp(`^(${[
  'mailto', 'tel', 'sms', 'mms', 'data', 'blob', 'javascript', 'vbscript', 'file', 'intent', 'otpauth', 'otpauth-migration',
  'webcal', 'webcals', 'magnet', 'geo', 'facetime', 'facetime-audio', 'callto', 'skype', 'sip', 'sips', 'xmpp', 'bitcoin',
  'ethereum', 'urn', 'news', 'nntp', 'tg', 'whatsapp', 'sgnl', 'msteams', 'zoommtg', 'zoomus', 'slack', 'spotify',
].join('|')}):`, 'i')

/** Sites whose paths are public pages (repositories, videos): links to them keep the path. */
const RESOURCE_HOSTS = /(^|\.)(github\.com|youtube\.com|youtu\.be)$/

/** A web address of another site keeps its origin (and its path only on GitHub and YouTube, like
 *  resource_link_clicked): a shared document, a meeting or a private page carries its secret in the path. Addresses
 *  of this site keep their path, redacted (redactPath), never the query or fragment. */
function webLink(value: string): string {
  try {
    const u = new URL(value)
    if (typeof window !== 'undefined' && u.host === window.location.host) return `${u.origin}${redactPath(u.pathname)}`
    return RESOURCE_HOSTS.test(u.hostname.replace(/^www\./, '')) ? `${u.origin}${u.pathname}` : u.origin
  } catch {
    return redactPath(stripQuery(value))
  }
}

function scrubText(value: string, key: string): string {
  const scheme = SCHEME.exec(value)?.[1]
  const link = LINK_KEY.test(key)
  if (scheme && !/^https?$/i.test(scheme) && (link || /^[a-z][a-z0-9+.-]*:\/\//i.test(value) || OPAQUE_SCHEME.test(value))) {
    return `${scheme.toLowerCase()}:`
  }
  if (WEB_URL.test(value)) return webLink(value)
  // a path of this site (a clicked link, autocapture's page path, the heatmap page view's current_url_path)
  if (value.startsWith('/') && !value.startsWith('//')) return redactPath(/[?#]/.test(value) ? stripQuery(value) : value)
  return (link || URL_LIKE.test(value)) && /[?#]/.test(value) ? stripQuery(value) : value
}

/** Every URL in a payload (current page, referrers, the href of a clicked link and its parents) without its query
 *  and fragment, other sites down to their origin, and links to anything but a web page down to their scheme; text
 *  the visitor typed or read never goes (autocapture keeps no text content). */
function scrub(value: unknown, depth = 0, key = ''): unknown {
  if (typeof value === 'string') return scrubText(value, key)
  if (depth > 8 || value === null || typeof value !== 'object') return value
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1, key))
  const out: Props = {}
  for (const [k, v] of Object.entries(value as Props)) out[k] = scrub(v, depth + 1, k)
  return out
}

/** Never sent: the page title (a member's name on a profile, a post title of a private community; Mixpanel's own
 *  page view adds it while heatmap data is recorded), the query string, text, and the ad click ids of the address. */
const DROPPED = ['current_url_search', 'current_page_title', 'mp_keyword', '$el_text', 'dclid', 'fbclid', 'gbraid', 'gclid',
  'ko_click_id', 'li_fat_id', 'msclkid', 'sccid', 'ttclid', 'twclid', 'wbraid']
/** What an autocaptured element may keep inside a private area: its kind and place, never a label, link or name. */
const PRIVATE_KEEP = new Set(['$tag_name', '$classes', '$nth_child', '$nth_of_type', '$attr-role', '$attr-type'])
const MARK = `$attr-${MASK_ATTR}`

function plainElement(el: unknown): unknown {
  if (!el || typeof el !== 'object') return el
  return Object.fromEntries(Object.entries(el as Props).filter(([k]) => PRIVATE_KEEP.has(k)))
}

/** An avatar (role img) is labelled with the member's name: its label never goes, wherever it is. */
function namelessImage(el: unknown): unknown {
  if (!el || typeof el !== 'object' || (el as Props)['$attr-role'] !== 'img') return el
  return Object.fromEntries(Object.entries(el as Props).filter(([k]) => k !== '$attr-aria-label'))
}

/** A click, submit, rage or dead click inside a private area (its element chain carries the mask attribute, captured
 *  by capture_extra_attrs): Mixpanel keeps where and on what kind of element, with no aria labels, hrefs or names. */
function privateClick(properties: Props): boolean {
  const chain = Array.isArray(properties.$elements) ? (properties.$elements as Props[]) : []
  const target = properties.$target as Props | undefined
  return chain.some((el) => !!el && MARK in el) || (!!target && MARK in target) || `$el_attr__${MASK_ATTR}` in properties
}

export function scrubEvent<T extends { properties?: Props }>(payload: T): T {
  const properties = scrub(payload.properties ?? {}) as Props
  for (const k of DROPPED) delete properties[k]
  if (Array.isArray(properties.$elements)) properties.$elements = properties.$elements.map(namelessImage)
  if (properties.$target) properties.$target = namelessImage(properties.$target)
  if (privateClick(properties)) {
    if (Array.isArray(properties.$elements)) properties.$elements = properties.$elements.map(plainElement)
    if (properties.$target) properties.$target = plainElement(properties.$target)
    for (const k of Object.keys(properties)) if (k.startsWith('$el_attr__') || k === '$el_id') delete properties[k]
    properties.private_area = true
  }
  return { ...payload, properties }
}

export function scrubPeople<T>(payload: T): T {
  return scrub(payload) as T
}

/** before_track: Mixpanel keeps events in browser storage until a batch goes, and a batch a closed or frozen page left
 *  behind is sent by a later page without the send hooks, so events are scrubbed before they are stored too. */
export function scrubTrack(event: string, properties?: Props): [string, Props] {
  return [event, scrubEvent({ properties: properties ?? {} }).properties ?? {}]
}

/** What session replay records as an empty box (no text, no attributes, nothing inside; mixpanel-browser 2.83 keeps
 *  only the size). Masking hides text but records every attribute as it is, so whatever carries a person or a secret
 *  in an attribute is blocked: media; the page's description, Open Graph and Twitter meta tags and structured data (the
 *  Pages middleware puts a profile's name or a post's title there) and the visitor's mg-geo tag (the viewport and
 *  charset tags stay, replays need them); chats and direct messages (data-mp-hide); the authenticator key, recovery codes and
 *  calendar feed (data-mp-block); avatars (role img, labelled with the member's name); links to profiles (the handle),
 *  to posts (the title), to certificates (the code), to other sites and to anything but a page of this site (a shared
 *  document, otpauth:, webcal:, mailto:), download links (a file name) and links whose query can carry a token, an
 *  address, search words or an affiliate's handle; the canonical link (a post's title); the member map's pins and
 *  popups (a name and a typed location); @mentions (data-label holds the name, data-id the user id); file pickers
 *  (rrweb records the picked file's name as their value); video embeds (data-video-id is all it takes to watch an
 *  unlisted or paid video); affiliate rows (the handle in their test id). */
export const REPLAY_BLOCK_SELECTOR = [
  'img', 'video', 'audio', 'canvas', 'iframe', 'object', 'embed', 'meta[property]', 'meta[name="description"]',
  'meta[name^="twitter:"]', 'meta[name="mg-geo"]', 'script[type="application/ld+json"]', 'link[rel="canonical"]',
  `[${BLOCK_ATTR}]`, `[${HIDE_ATTR}]`, '[role="img"]',
  'a[href^="/u/"]', 'a[href*="/post/"]', 'a[href^="/certificates/"]', 'a:not([href^="/"])', 'a[href^="//"]',
  'a[download]', 'a[href*="next="]', 'a[href*="invite"]', 'a[href*="token"]', 'a[href*="email"]', 'a[href*="code="]',
  'a[href*="q="]', 'a[href*="ref="]',
  '.leaflet-marker-icon', '.leaflet-popup', '[data-type="mention"]', 'input[type="file"]', '[data-video-embed]',
  '[data-video-id]', '[data-testid^="affiliate-row-"]',
].join(', ')

/** The mixpanel-browser 2.83 configuration (exported for tests): localStorage only, no automatic page views
 *  (page_view below carries the route), autocapture of clicks, form submits, scroll depth, rage and dead clicks and
 *  page leaves (with the time spent) without any text or input value (inside private areas without labels, links or
 *  names either, scrubEvent), and session replay with all text and every input masked (static navigation shown,
 *  UNMASK_AREA), REPLAY_BLOCK_SELECTOR recorded as empty boxes and no console or network capture. Replay starts from
 *  syncReplay(), never by itself. Heatmap data: while a replay records, Mixpanel adds its own $mp_web_page_view (the
 *  page title dropped, DROPPED) and marks clicks for its heatmaps. */
export function mixpanelConfig() {
  return {
    persistence: 'localStorage' as const,
    ignore_dnt: false,
    track_pageview: false,
    save_referrer: false,
    store_google: false,
    track_marketing: false,
    skip_first_touch_marketing: true,
    property_blacklist: DROPPED,
    autocapture: {
      pageview: false as const,
      click: true,
      submit: true,
      scroll: true,
      input: false,
      rage_click: true,
      dead_click: true,
      page_leave: true,
      capture_text_content: false,
      scroll_depth_percent_checkpoints: [25, 50, 75, 100],
      block_selectors: [`[${BLOCK_ATTR}]`],
      block_attrs: ['title', 'value', 'placeholder'],
      capture_extra_attrs: [MASK_ATTR],
    },
    record_sessions_percent: 0,
    record_mask_all_inputs: true,
    record_mask_all_text: true,
    record_unmask_text_selector: [`[${UNMASK_ATTR}]`],
    record_block_selector: REPLAY_BLOCK_SELECTOR,
    record_console: false,
    record_network: false,
    record_canvas: false,
    record_collect_fonts: false,
    record_inline_images: false,
    record_heatmap_data: true,
    record_idle_timeout_ms: 30 * 60 * 1000,
    hooks: { before_track: scrubTrack, before_send_events: scrubEvent, before_send_people: scrubPeople },
    error_reporter: () => undefined,
  }
}

let mixpanel: Mixpanel | null = null
let sentry: Sentry | null = null
let loading: Promise<void> | null = null
let queue: Array<(mp: Mixpanel) => void> = []
let sampled: boolean | null = null
let replaying = false
let context: Props = {}
let identity: AnalyticsUser | null = null
/** /api/auth/me answered "nobody is signed in": a person's id Mixpanel still holds from an earlier visit goes */
let signedOut = false
/** The page a visitor is on while analytics are not allowed yet: its page_view goes once they are (Accept there). */
let unsentPage: string | null = null

function run(fn: (mp: Mixpanel) => void): void {
  try {
    if (mixpanel) fn(mixpanel)
  } catch {
    /* analytics must never break the app */
  }
}

/** Runs now while analytics run; nothing (not even a super property) touches Mixpanel after a no. */
function live(fn: (mp: Mixpanel) => void): void {
  if (analyticsAllowed()) run(fn)
}

/** Runs now, or once Mixpanel is loaded and running (the page's first events wait in memory while the SDK loads);
 *  dropped (never queued) while the visitor has not allowed analytics. */
function withMixpanel(fn: (mp: Mixpanel) => void): void {
  if (!analyticsConfigured() || !useConsentStore.getState().decision.allowed) return
  if (mixpanel && analyticsAllowed()) run(fn)
  else if (queue.length < 200) queue.push(fn)
}

/** Mixpanel's id is a person's (after identify) rather than its random visitor id ("$device:..."). */
function personId(mp: Mixpanel): boolean {
  try {
    return !String(mp.get_distinct_id() ?? '').startsWith('$device:')
  } catch {
    return false
  }
}

/** Mixpanel's own opt out flag from an earlier "no" goes once the visitor said yes, without its $opt_in event (not in
 *  the event dictionary). Never while analytics are not allowed. */
function optBackIn(mp: Mixpanel): void {
  if (analyticsAllowed() && mp.has_opted_out_tracking()) mp.opt_in_tracking({ track: () => undefined })
}

/** Loads Mixpanel and starts tracking when allowed. Safe to call more than once. */
export async function startProductAnalytics(): Promise<void> {
  const token = mixpanelToken()
  if (!token || !analyticsAllowed()) return
  if (mixpanel) {
    run(optBackIn)
    applyState()
    return
  }
  loading ??= import('mixpanel-browser')
    .then((mod) => {
      const mp = mod.default
      if (!replayWanted()) forgetReplayTab(token)
      mp.init(token, mixpanelConfig())
      mixpanel = mp
      // the answer may have changed while the SDK loaded: check it before anything else (code review 10)
      if (!analyticsAllowed()) return stopProductAnalytics()
      optBackIn(mp)
      // a session that ended in another tab or on another device left that person's id in this browser
      if (signedOut && !identity && personId(mp)) {
        dropReplay(mp)
        mp.reset()
      }
      applyState()
      // Mixpanel starts or resumes a replay by itself a moment later (its recording registry): check it again then
      const recorderStart = mp as unknown as { __get_recording_init_promise?: () => Promise<unknown> | undefined }
      void Promise.resolve(recorderStart.__get_recording_init_promise?.()).then(() => syncReplay(), () => undefined)
    })
    .catch(() => undefined)
    .finally(() => {
      loading = null
    })
  await loading
}

/** After a start: super properties, first touch, the signed in person, whatever waited, the page the visitor
 *  accepted on, and replay. */
function applyState(): void {
  run((mp) => {
    registerSafeUrl(mp)
    mp.register_once(firstTouchProps(), '')
    mp.register({ ...baseContext(), ...context }, { persistent: false })
    if (identity) identify(mp, identity)
  })
  const pending = queue
  queue = []
  pending.forEach((fn) => run(fn))
  if (unsentPage !== null) {
    const page = unsentPage
    unsentPage = null
    sendPageView(page)
  }
  syncReplay()
}

/** The address bar without its query string, as super properties: Mixpanel would otherwise add the full address to
 *  every event before any hook sees it, and keep it in browser storage until the batch goes. */
function registerSafeUrl(mp: Mixpanel): void {
  mp.register(safeUrlProps(), { persistent: false })
}

/** Turns product analytics off at once: Mixpanel's opt out first (from then on it sends nothing, the replay batch it
 *  still held included; its senders stop and its persistence goes), the replay dropped and its browser data cleared.
 *  Nothing leaves after a no. */
export function stopProductAnalytics(): void {
  queue = []
  run((mp) => {
    mp.opt_out_tracking({ delete_user: false }) // clear_persistence is on by default; it also ends the replay
    dropReplay(mp)
  })
  replaying = false
  clearMixpanelStorage()
  // Mixpanel's send queues write to storage under a lock a moment after a call (an identify right before an answer
  // from another device arrived, for example): clear again once those writes settled, while the answer is still no
  if (mixpanel) {
    for (const ms of [250, 1500, 5000]) {
      setTimeout(() => {
        if (!analyticsAllowed()) clearMixpanelStorage()
      }, ms)
    }
  }
}

const MIXPANEL_KEY = /^(mp_|__mpq_|__mprec_)/

/** Mixpanel's keys in localStorage (mp_<token>_mixpanel, its send queues and replay batches) and sessionStorage (the
 *  tab id of replays), and its replay database. Its own opt out flag (__mp_opt_in_out_<token>) stays as the record of
 *  the no. Without an argument both storages are swept. */
export function clearMixpanelStorage(storage?: Storage): void {
  for (const store of storage ? [storage] : [webStorage('localStorage'), webStorage('sessionStorage')]) {
    try {
      const keys: string[] = []
      for (let i = 0; i < (store?.length ?? 0); i++) {
        const k = store?.key(i)
        if (k && MIXPANEL_KEY.test(k)) keys.push(k)
      }
      keys.forEach((k) => store?.removeItem(k))
    } catch {
      /* storage blocked: there is nothing to clear */
    }
  }
  try {
    globalThis.indexedDB?.deleteDatabase('mixpanelBrowserDb')
  } catch {
    /* no IndexedDB: nothing was kept there either */
  }
}

function webStorage(name: 'localStorage' | 'sessionStorage'): Storage | undefined {
  try {
    return globalThis[name]
  } catch {
    return undefined
  }
}

/** Session replay follows the analytics decision: on by default where consent does not come first, after a yes
 *  where it does. */
function replayConsented(): boolean {
  return useConsentStore.getState().decision.allowed
}

/** This page load may record: in the sample, with an explicit yes, and an address without a secret. */
function replayWanted(): boolean {
  sampled ??= Math.random() * 100 < replayPercent()
  const { search, pathname, hash } = window.location
  return sampled && replayConsented() && !hasSecretQuery(search, pathname, hash)
}

/** Whether Mixpanel's recorder is running now (whoever started it). */
function recording(mp: Mixpanel): boolean {
  try {
    return !!(mp.get_session_recording_properties() as { $mp_replay_id?: string }).$mp_replay_id
  } catch {
    return false
  }
}

/** Mixpanel resumes, by itself, the replay this tab recorded less than 30 idle minutes ago on every full page load (an
 *  invite link opened in the same tab, the way back from Stripe Checkout or Google): where this page may not record,
 *  the tab gets a new replay identity before Mixpanel starts, so there is nothing to resume. */
function forgetReplayTab(token: string): void {
  try {
    webStorage('sessionStorage')?.removeItem(`mp_tab_id_mixpanel_${token}`)
  } catch {
    /* storage blocked: Mixpanel cannot resume a replay either */
  }
}

type Recorder = {
  activeRecording?: { batcher?: { clear?: () => unknown } } | null
  recordingRegistry?: { clearActiveRecording?: () => unknown }
  _stopCurrentRecording?: (skipFlush: boolean, disable: boolean) => unknown
}

/** Ends the replay without uploading what it still holds (mixpanel-browser 2.83 uploads it on stop_session_recording,
 *  and that buffer can hold the page that just opened with a secret in its address): rrweb stops, the waiting batch is
 *  cleared (again a moment later, for a throttled write still on its way) and the tab's recording is closed, so the
 *  next page load neither resumes nor uploads it. A recorder that was still starting is checked again (recheckReplay). */
function dropReplay(mp: Mixpanel, recheck = true): void {
  if (recheck) recheckReplay(mp)
  const recorder = (mp as unknown as { __get_recorder?: () => Recorder | null | undefined }).__get_recorder?.()
  if (typeof recorder?._stopCurrentRecording !== 'function') return // no recorder loaded: nothing records yet
  const batcher = recorder.activeRecording?.batcher
  const clear = () => void Promise.resolve(batcher?.clear?.()).catch(() => undefined)
  void Promise.resolve(recorder._stopCurrentRecording(true, true))
    .then(() => {
      clear()
      return recorder.recordingRegistry?.clearActiveRecording?.()
    })
    .catch(() => undefined)
  for (const ms of [400, 2000, 6000]) setTimeout(clear, ms)
}

/** When replay is checked again after a start or a stop: mixpanel-browser 2.83 starts its recorder a moment after the
 *  call, once its storage answers, so a recorder that came up late on an address it may not record (or after a no) is
 *  ended there, before its first batch goes at 10 s (re-audit minor 8). */
const REPLAY_RECHECK_MS = [1500, 4000, 9000]

function recheckReplay(mp: Mixpanel): void {
  for (const ms of REPLAY_RECHECK_MS) {
    setTimeout(() => {
      if (mixpanel !== mp || !recording(mp)) return
      if (!replaying || !analyticsAllowed() || !replayWanted()) {
        replaying = false
        run((m) => dropReplay(m, false))
      }
    }, ms)
  }
}

/** Session replay follows the sample, the explicit yes and the address bar: it never records while the URL holds
 *  anything but a safe query parameter, and stops without uploading when it should not record. */
function syncReplay(): void {
  if (!mixpanel || !analyticsAllowed()) return
  const mp = mixpanel
  if (replayWanted()) {
    if (!replaying) {
      run((m) => m.start_session_recording())
      recheckReplay(mp)
    }
    replaying = true
  } else if (replaying || recording(mp)) {
    run(dropReplay)
    replaying = false
  }
}

// Consent changes take effect at once: a yes starts, a no stops and clears.
useConsentStore.subscribe((state, prev) => {
  const now = effectiveAllowed(state)
  if (now !== effectiveAllowed(prev)) {
    if (now) void startProductAnalytics()
    else stopProductAnalytics()
  } else if (state.decision.reason !== prev.decision.reason) {
    syncReplay()
    if (identity && analyticsAllowed()) live((mp) => identity && identify(mp, identity))
  }
})

// The address bar changed (a link, a redirect, back and forward, a query the page set): the query free address for
// the next events and the replay check happen at once, in the same task, before the new page is drawn, so a recording
// never holds a page whose address carries a secret, not even for a moment.
if (typeof window !== 'undefined' && typeof window.history?.pushState === 'function') {
  const slot = window as unknown as { __mgsAddressChanged?: () => void }
  const first = !slot.__mgsAddressChanged
  // one hook for the page; the latest copy of this module answers it (tests load the module again)
  slot.__mgsAddressChanged = () => {
    live(registerSafeUrl)
    syncReplay()
  }
  if (first) {
    const changed = () => slot.__mgsAddressChanged?.()
    for (const name of ['pushState', 'replaceState'] as const) {
      const native = window.history[name]
      window.history[name] = function (this: History, ...args: Parameters<History['pushState']>) {
        native.apply(this, args)
        changed()
      }
    }
    window.addEventListener('popstate', changed)
    window.addEventListener('hashchange', changed)
  }
}

/** Starts Sentry when configured, and Mixpanel when configured and allowed. Safe to call more than once. */
export async function initAnalytics(): Promise<void> {
  const jobs: Promise<unknown>[] = [startProductAnalytics()]
  if (SENTRY_DSN && !sentry) {
    jobs.push(
      import('@sentry/react').then((mod) => {
        mod.init({
          dsn: SENTRY_DSN,
          environment: import.meta.env.MODE,
          release: BUILD,
          tracesSampleRate: 0,
          ignoreErrors: SENTRY_IGNORE_ERRORS,
          denyUrls: SENTRY_DENY_URLS,
          beforeSend(event) {
            if (event.request) {
              if (event.request.url) event.request.url = stripQuery(event.request.url)
              delete event.request.query_string
            }
            return event
          },
          beforeBreadcrumb(crumb) {
            const data = crumb.data
            if (data) for (const k of ['from', 'to', 'url']) if (typeof data[k] === 'string') data[k] = stripQuery(data[k])
            return crumb
          },
        })
        sentry = mod
      }),
    )
  }
  await Promise.allSettled(jobs)
}

// ------------------------------------------------------------------------------------------------ what is sent

export type AnalyticsCommunity = { slug: string; role: string; status: string; level?: number; points?: number }
export type AnalyticsUser = {
  id: string
  name?: string
  email?: string
  handle?: string
  platformRole?: string
  language?: string | null
  timezone?: string | null
  theme?: string | null
  emailVerified?: boolean
  createdAt?: string | null
  communities?: AnalyticsCommunity[]
}

/** Same key as features/auth/utils FIRST_TOUCH_KEY: the first visit's campaign tags, written only when allowed. */
const FIRST_TOUCH_KEY = 'mgs-first-touch'

/** First touch campaign tags, referrer domain and landing page, as Mixpanel super properties (register_once). */
export function firstTouchProps(storage: Storage | undefined = globalThis.localStorage): Props {
  let ft: Record<string, string | null> | null = null
  try {
    const raw = storage?.getItem(FIRST_TOUCH_KEY)
    ft = raw ? (JSON.parse(raw) as Record<string, string | null>) : null
  } catch {
    ft = null
  }
  if (!ft) return {}
  let domain = ft.referringDomain || null
  try {
    domain ??= ft.referrer ? new URL(ft.referrer).hostname : null
  } catch {
    domain = null
  }
  const props: Props = {
    initial_utm_source: ft.utmSource, initial_utm_medium: ft.utmMedium, initial_utm_campaign: ft.utmCampaign,
    initial_utm_content: ft.utmContent, initial_utm_term: ft.utmTerm,
    initial_landing_page: ft.landing ? redactPath(stripQuery(ft.landing)) : ft.landing,
    $initial_referrer: (ft.referrer && scrubText(ft.referrer, '$initial_referrer')) || '$direct',
    $initial_referring_domain: domain || '$direct',
  }
  return Object.fromEntries(Object.entries(props).filter(([, v]) => v !== null && v !== undefined && v !== ''))
}

/** Called once the first touch was saved (it waits for consent where consent comes first). */
export function registerFirstTouch(): void {
  withMixpanel((mp) => mp.register_once(firstTouchProps(), ''))
}

function baseContext(): Props {
  return { app_build: BUILD ?? 'dev', logged_in: !!identity }
}

/** Super properties on every later event: route, community and viewer role, locale, theme (non persistent). */
export function setAnalyticsContext(props: Props): void {
  context = { ...context, ...props }
  withMixpanel((mp) => mp.register(props, { persistent: false }))
}

/** Every browser event (docs/analytics.md has where each fires and its properties): ids, slugs and counts, never
 *  what people wrote. Server events (signups, joins, money, levels) come from the API (services/product_analytics). */
export const BROWSER_EVENTS = [
  'page_view', 'signup_started', 'login_started', 'about_viewed', 'about_video_played', 'about_video_progress',
  'join_clicked', 'waitlist_join_clicked', 'checkout_started', 'plan_switch_started', 'course_opened', 'lesson_viewed',
  'lesson_completed', 'quiz_submitted', 'resource_link_clicked', 'search_performed', 'post_composer_opened',
  'level_gate_shown', 'post_created', 'comment_created', 'like_toggled', 'poll_voted', 'chat_opened', 'message_sent',
  'event_rsvp', 'notification_opened', 'report_submitted', 'welcome_dialog_completed', 'start_checklist_step',
  'settings_changed', 'language_changed', 'theme_changed', 'invite_link_copied',
] as const
export type BrowserEvent = (typeof BROWSER_EVENTS)[number]

/** `beacon`: the page is about to leave (Google sign in, Stripe Checkout), so the event goes at once by sendBeacon. */
export function track(event: BrowserEvent, props?: Props, options?: { beacon?: boolean }): void {
  withMixpanel((mp) =>
    options?.beacon ? mp.track(event, props, { send_immediately: true, transport: 'sendBeacon' }) : mp.track(event, props),
  )
}

/** resource_link_clicked: the link's host, plus its path only for github.com and youtube.com (public repositories and
 *  videos); never the query or the link text. */
export function trackResourceLink(url: string, props?: Props): void {
  let host: string
  let path: string | undefined
  try {
    const u = new URL(url, window.location.origin)
    if (!/^https?:$/.test(u.protocol)) return
    host = u.hostname.replace(/^www\./, '')
    if (RESOURCE_HOSTS.test(host)) path = u.pathname
  } catch {
    return
  }
  track('resource_link_clicked', { host, path, internal: host === window.location.hostname, ...props })
}

/** A new route: the route, community and viewer role super properties, the query free address and the replay check,
 *  at once (its page_view may wait, trackPageView). */
export function enterPage(pathname: string, role: string | null = null): void {
  const info = routeInfo(pathname)
  setAnalyticsContext({ route: info.route, community: info.community, community_role: role })
  live(registerSafeUrl)
  syncReplay()
}

/** page_view: the path without its query, the route name and the community slug, and the timer whose
 *  $mp_page_leave tells the seconds spent on the page. While analytics are not allowed yet the page is remembered
 *  (in memory, unsent) and its page_view goes if the visitor accepts there. */
function sendPageView(pathname: string): void {
  if (!analyticsConfigured()) return
  if (!useConsentStore.getState().decision.allowed) {
    unsentPage = pathname
    return
  }
  unsentPage = null
  withMixpanel((mp) => {
    registerSafeUrl(mp)
    mp.track('page_view', { path: redactPath(stripQuery(pathname)), ...routeInfo(pathname) })
    mp.time_event('$mp_page_leave') // the page's $mp_page_leave carries $duration: the seconds spent on it
  })
}

/** How long a route must stay before its page_view goes (RootLayout): a route that redirects at once (the home page
 *  to the last community, a sign in guard) is never counted as a page someone saw. */
export const PAGE_VIEW_SETTLE_MS = 1000

/** page_view on every route change (enterPage at once, the event after `settleMs`). Returns a cancel function: the
 *  page_view of a route left before then never goes. */
export function trackPageView(pathname: string, role: string | null = null, settleMs = 0): () => void {
  enterPage(pathname, role)
  if (settleMs <= 0) {
    sendPageView(pathname)
    return () => undefined
  }
  const timer = setTimeout(() => sendPageView(pathname), settleMs)
  return () => clearTimeout(timer)
}

/** The query string changed: the query free address for the next events, and replay pauses while the address bar
 *  holds anything but a safe query parameter (it comes back once the page removed it). */
export function syncReplayWithUrl(): void {
  live(registerSafeUrl)
  syncReplay()
}

let profileKey = ''

function identify(mp: Mixpanel, user: AnalyticsUser): void {
  mp.register({ logged_in: true }, { persistent: false })
  let restart = false
  if (mp.get_distinct_id() !== user.id) {
    // another person is still identified in this browser (their session ended unseen): their replay goes unsent and a
    // new visitor id comes first, so nothing of theirs is joined to this person (re-audit minor 9)
    if (personId(mp)) {
      dropReplay(mp)
      replaying = false
      restart = true
      mp.reset()
    }
    mp.identify(user.id)
  }
  if (restart) syncReplay()
  const active = (user.communities ?? []).filter((c) => c.status === 'active')
  const profile = {
    $name: user.name, $email: user.email, handle: user.handle, platform_role: user.platformRole,
    locale: user.language, timezone: user.timezone, theme: user.theme, email_verified: user.emailVerified,
    communities: active.map((c) => c.slug), community_roles: active.map((c) => `${c.slug}:${c.role}`),
    max_level: Math.max(0, ...active.map((c) => c.level ?? 0)),
    total_points: active.reduce((sum, c) => sum + (c.points ?? 0), 0),
  }
  const key = JSON.stringify(profile)
  // once per change, not on every refresh of /api/auth/me
  if (key !== profileKey) {
    profileKey = key
    mp.people.set(profile)
    mp.people.set_once({ ...(user.createdAt ? { $created: user.createdAt.slice(0, 19) } : {}), ...profileFirstTouch() })
  }
}

function profileFirstTouch(): Props {
  const props = firstTouchProps()
  delete props.$initial_referrer
  delete props.$initial_referring_domain
  return props
}

/** identify(user id) on sign in, sign up and session restore, with the people profile. */
export function identifyUser(user: AnalyticsUser): void {
  identity = user
  signedOut = false
  withMixpanel((mp) => identify(mp, user))
  sentry?.setUser({ id: user.id })
}

/** Sign out, or a session that ended (code review 1): a new random visitor id and nothing of the last person left in
 *  this browser's analytics state: their identity, profile and whatever still waited for Mixpanel. Mixpanel's reset
 *  runs even while analytics are off: an opt out only deletes the stored copy of the ids, and a later yes in this tab
 *  would write the last person's back (reset sends nothing while opted out). Call it before the consent store's
 *  signOut. A new replay starts for the visitor when they may be recorded. */
export function resetAnalytics(): void {
  identity = null
  profileKey = ''
  queue = []
  unsentPage = null
  run((mp) => {
    dropReplay(mp) // the last person's replay goes unsent: Mixpanel's reset would upload it under their id
    mp.reset()
    registerSafeUrl(mp)
    mp.register({ ...baseContext(), ...context, logged_in: false, community_role: null }, { persistent: false })
  })
  replaying = false
  syncReplay()
  sentry?.setUser(null)
}

/** /api/auth/me answered that nobody is signed in. A person known in this tab (the session ended: revoked, expired,
 *  signed out in another tab, the account deleted) is reset at once; a person's id Mixpanel kept from an earlier
 *  visit goes now, or as soon as Mixpanel loads. A visitor who was never signed in keeps their random id. */
export function markSignedOut(): void {
  if (identity) {
    resetAnalytics()
    return
  }
  signedOut = true
  run((mp) => {
    if (personId(mp)) resetAnalytics()
  })
}

/** The signed in person known to analytics in this tab, if any (lib/auth ends their session). */
export function analyticsUserId(): string | null {
  return identity?.id ?? null
}

// Route names of src/router.tsx: platform pages by their first segment, community pages by the one after the slug.
const PLATFORM_ROUTES: Record<string, string> = {
  '': 'home', discover: 'discover', login: 'login', signup: 'signup', 'forgot-password': 'forgot_password',
  'reset-password': 'reset_password', 'verify-email': 'verify_email', certificates: 'certificate',
  unsubscribe: 'unsubscribe', u: 'profile', _fake: 'fake_checkout', terms: 'terms', privacy: 'privacy',
  support: 'support', accessibility: 'accessibility', resources: 'resources', create: 'create_community',
  settings: 'settings', chat: 'chat', notifications: 'notifications', appeals: 'appeal', admin: 'admin',
}
const COMMUNITY_ROUTES: Record<string, string> = {
  '': 'feed', post: 'post', classroom: 'classroom', calendar: 'calendar', members: 'members', map: 'map',
  leaderboards: 'leaderboards', about: 'about', plans: 'plans', affiliates: 'affiliates', search: 'search',
  settings: 'community_settings', live: 'live',
}
const SLUG = /^[a-z0-9-]{3,48}$/

export type RouteInfo = { route: string; community: string | null; section: string | null }

/** The route name, community slug and settings section of a path (page_view and the route super property). */
export function routeInfo(pathname: string): RouteInfo {
  const parts = stripQuery(pathname).split('/').filter(Boolean).map((p) => p.toLowerCase())
  const [first = '', second = '', third = ''] = parts
  if (Object.hasOwn(PLATFORM_ROUTES, first)) {
    const section = first === 'settings' || first === 'admin' ? second || null : null
    return { route: PLATFORM_ROUTES[first], community: null, section }
  }
  if (!SLUG.test(first)) return { route: 'not_found', community: null, section: null }
  const named = Object.hasOwn(COMMUNITY_ROUTES, second) ? COMMUNITY_ROUTES[second] : 'not_found'
  const route = second === 'classroom' && third ? 'course' : named
  return { route, community: first, section: second === 'settings' ? third || null : null }
}

/** Reports an unexpected error to Sentry when it is configured. */
export function reportError(error: unknown, context?: Props): void {
  if (!sentry) return
  try {
    sentry.captureException(error, context ? { extra: context } : undefined)
  } catch {
    /* ignore */
  }
}
